import polyfill from "@oddbird/css-anchor-positioning/fn";
import PropTypes from "prop-types";
import React, { useEffect } from "react";

import { classNames } from "@swa-ui/string";

import styles from "./AnchorTarget.module.scss";

/**
 * AnchorTarget renders children content adjacent to another DOM element (Anchor). Anchor utilizes
 * the native browser anchor capabilities, but only allows a subset of its options in pursuit of
 * simplification. Anchor and AnchorTarget must be used together.
 *
 * NOTE:
 * A classname must be given which contains a position-anchor property. This should be something
 * like:
 *
 * .classPassedToAnchorTarget {
 *    position-anchor: --my-anchor;
 *  }
 *
 *  This name, '--my-anchor' in the example above, must match the anchor-name given to the Anchor
 *  component via its className. See Anchor for info.
 *
 * TODO:
 * The className with position-anchor requirement is temporary until browser support is widely
 * implemented. This requirement can then go away with PHX-2817.
 */

export const AnchorTarget = (props) => {
  const { autoFlip, children, className, location = "hidden", name, transform } = props;

  useEffect(() => {
    if (!("anchorName" in document.documentElement.style)) {
      polyfill();
    }
  }, [location]);

  return (
    <div className={getClass()} style={getStyle()}>
      {children}
    </div>
  );

  function getClass() {
    return classNames(
      {
        [styles.above]: location === "above",
        [styles.aboveLeft]: location === "above left",
        [styles.aboveRight]: location === "above right",
        [styles.autoFlip]: autoFlip,
        [styles.below]: location === "below",
        [styles.belowLeft]: location === "below left",
        [styles.belowRight]: location === "below right",
        [styles.center]: location === "center",
        [styles.hidden]: location === "hidden",
        [styles.left]: location === "left",
        [styles.right]: location === "right",
      },
      styles.target,
      className
    );
  }

  function getStyle() {
    return {
      positionAnchor: `--${name}`,
      transform,
    };
  }
};

AnchorTarget.propTypes = {
  /**
   * Indicates to move target content to opposite side of location when content will not fit
   * in the given location. For example, if location is set to "left", but there isn't enough space
   * in the anchor's container on the left to fit the content, the target will be display on the right.
   */
  autoFlip: PropTypes.bool,

  /**
   * Target content to be placed adjacent to the anchor.
   *
   * For example:
   * <Anchor className={styles.anchor} name="my-name">Anchor</Anchor>
   * <AnchorTarget className={styles.target} location="right" name="my-name">Target</AnchorTarget>
   */
  children: PropTypes.node.isRequired,

  /**
   * Additional classes for positioning the component. It must contain a position-anchor property.
   * Something like:
   * position-anchor: --my-anchor;
   */
  className: PropTypes.string.isRequired,

  /** Which adjacent side to render target relative to anchor. */
  location: PropTypes.oneOf([
    "above",
    "above left",
    "above right",
    "below",
    "below left",
    "below right",
    "center",
    "hidden",
    "left",
    "right",
  ]),

  /**
   * Name that will be used to tie Anchor and AnchorTarget together. Both Anchor and AnchorTarget
   * must receive the same name prop value.
   */
  name: PropTypes.string.isRequired,

  /** String to set outmost div element's transform property. Something like: "translateX(50px)". */
  transform: PropTypes.string,
};
